import { type Composer } from 'vue-i18n'
import type { MaybeRef } from 'vue'
import type { ReferenceObject, SchemaObject } from 'openapi-typescript'
import type { components } from '../types/_api'
import { formatFromSchema } from './format'

type UIStructure = components['schemas']['UI-Structure']

/**
 * Determines if a field value should be translated based on UI structure metadata.
 *
 * Checks the ui_structure.structure[resource].skip_translation array to see if
 * the field should skip translation.
 *
 * @param fieldPath - The field path (e.g., 'locale_tag')
 * @param uiStructure - The UI structure from user--extended-data
 * @param resource - The resource name
 * @returns true if the value should be translated, false if it should be skipped
 */
export function shouldTranslate(fieldPath: string, uiStructure?: UIStructure, resource?: string): boolean {
  if (resource == null || uiStructure == null) return true

  const resourceStructure = uiStructure.structure?.[resource]
  if (resourceStructure == null) return true

  const skipTranslation = resourceStructure.skip_translation ?? []
  return !skipTranslation.includes(fieldPath)
}

export function localeKey(
  key: string,
  locations?: string[] | string,
  translate = false,
  i18n?: Composer<Record<string, unknown>>
): string {
  if (!key) return key
  if (!i18n) return key

  const allowed = /^[a-zA-Z0-9._-]+$/
  if (!allowed.test(key)) return key

  const locs: string[] = []
  if (locations != null) {
    locs.push(...(Array.isArray(locations) ? locations : [locations]))
  }
  if (!locs.includes('common')) locs.push('common')

  /*
   * Here we want to consider nested keys, for example:
   * common.case.type should result in key candidates:
   *     common.case.type, case.type, type
   *
   * If the original key includes dots, we will also try the version with all
   * dots replaced by underscores:
   *     common_case_type, common.case.type, case.type, type
   *
   * Which will later be tried with the various prefixes, for example given
   * the locations item and common:
   *     common.common_case_type, item.common_case_type,
   *     common.common.case.type, item.common.case.type,
   *     common.case.type,        item.case.type,
   *     common.type,             item.type
   */
  const keyParts = key.toLowerCase().split('.')
  const keyCandidates: string[] = []

  if (key.includes('.')) {
    keyCandidates.push(key.replaceAll('.', '_'))
  }

  for (let idx = 0; idx < keyParts.length; idx++) {
    keyCandidates.push(keyParts.slice(idx).join('.'))
  }

  for (const candidate of keyCandidates) {
    for (const location of locs) {
      const locKey = `${location}.${candidate}`

      if (i18n.t(locKey) !== locKey) {
        return translate ? i18n.t(locKey) : locKey
      }
    }

    if (i18n.t(candidate) !== candidate) {
      return translate ? i18n.t(candidate) : candidate
    }
  }

  return key
}

export function getResourceTranslateLocations(resource?: string) {
  // TODO: calculate resource title from swagger + .toLocaleLowerCase().replaceAll('-', '_')
  return resource ? [resource, 'item', 'common'] : ['item', 'common']
}

/**
 * Resolves a schema enum value to its display label: formats the raw value via
 * the resource schema, then resolves the field-scoped locale key
 * (`<key>.<value>`, e.g. `status.debt_collection`) before the resource/`item`/
 * `common` fallbacks. Single source of truth shared by the list chip (useChip),
 * the Status filter (SelectFilter) and the selected-filter chip (FilterChip).
 * Pure — schema and i18n are injected.
 */
export function resolveEnumLabel(
  rawValue: string,
  key: string,
  resource: string | undefined,
  schema: MaybeRef<SchemaObject | ReferenceObject | undefined>,
  i18n: Composer<Record<string, unknown>>
): string {
  const formattedLabel = formatFromSchema(rawValue, key, schema).value
  const locations = [key, ...getResourceTranslateLocations(resource)]

  // Try the full value first (e.g. `cases__debt_collection`) — this matches the
  // list-view chip verbatim. If unresolved, fall back to the post-`__` suffix.
  // `__` is a `<group>__<value>` namespacing convention from the API; some
  // namespaced enums (notably `category`) only have locale keys for the suffix,
  // so dropping the split would regress them to raw text. localeKey only splits
  // on `.`, so this `__` fallback must live here.
  const full = localeKey(formattedLabel, locations, true, i18n)
  if (full !== formattedLabel) return full

  // noUncheckedIndexedAccess → split()[1] is `string | undefined`; a length
  // check would not narrow it, so guard explicitly.
  const suffix = formattedLabel.split('__')[1]
  if (suffix != null && suffix !== '') {
    const fromSuffix = localeKey(suffix, locations, true, i18n)
    if (fromSuffix !== suffix) return fromSuffix
  }
  return full
}
